import React, { useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Campaign,
  CampaignInventory,
  Character,
  CharacterInventory,
  // Character,
  MagicItemKeyword,
  PseudoItem,
  Spell,
  SpellLimitation,
} from "@dndModels";
import { ALL_DRAGONS, CREATURE_TYPES } from "@dndModels/Constants";

// import API from "../../../utilities/system_api";
import DataUtilities from '@dndUtilities/data_utilities';
import { makeStyles } from "@utilities/theme_utilities";

import ViewItem from "@dndComponents/display/ViewItem";
import ButtonBox from "@components/input/buttons/ButtonBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectSpellBox from "@dndComponents/select/SelectSpellBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props {
  char?: Character | Campaign;
  inventory?: CharacterInventory | CampaignInventory;
  log_it?: boolean;
  pseudo_item: PseudoItem;
  mik: MagicItemKeyword;
  onChange: (changed: PseudoItem) => void;
}

// const api = API.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterManageInventoryItemMIK({
  char,
  inventory,
  log_it = false,
  pseudo_item,
  mik,
  onChange,
}: Props) {
  const [extra_string, setExtraString] = useState("");
  const [extra_spell, setExtraSpell] = useState<Spell | null>(null);
  const [show_details, setShowDetails] = useState(false);
  const [show_spell, setShowSpell] = useState(false);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        color: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
        border: "1px solid"
      },
    });
  });

  const { classes, cx } = useStyles();
    
  const isDisabled = (mik: MagicItemKeyword) => {
    if (mik.min_rarity === 5) { // Artifacts can't be combined with MIKS
      return pseudo_item.magic_item_keyword_ids.length > 0 || pseudo_item.rarity > 0;
    } else if (mik.min_rarity + pseudo_item.rarity > 4) {
      return true; // Past Legendary
    } else if (mik.name.includes("+") && pseudo_item.name.includes("+")) {
      return true; // Only 1 +X mik
    } else if (mik.override_weapon_damage_type !== "" && pseudo_item.override_weapon_damage_type !== "") {
      return true; // Only 1 override weapon damage type
    } else if (pseudo_item.base_item && (pseudo_item.base_item.min_rarity !== pseudo_item.base_item.max_rarity || pseudo_item.varying_mik) && mik.min_rarity !== mik.max_rarity) {
      return true; // Only 1 varying rarity part
    }
    return (!["","None"].includes(mik.extra_base_type) && pseudo_item.extra !== "");
  }

  const has_it = pseudo_item.keyword_ids.includes(mik._id);

  if (mik.extra_base_type === "None") {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="clickable"
          onClick={() => {
            setShowDetails(!show_details);
          }}
          size={{
            xs: 8
          }}>
          <ViewItem item={mik} />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          { !has_it && isDisabled(mik) ?
            <ButtonBox
              name="Incompatible"
              disabled
              onClick={() => {}}
            />
          :
            <ToggleButtonBox
              name={ has_it ? "Included" : "Include" }
              value={has_it}
              onToggle={() => {
                const new_pseudo_item = pseudo_item.clone();
                if (new_pseudo_item.keyword_ids.includes(mik._id)) {
                  new_pseudo_item.magic_item_keywords = new_pseudo_item.magic_item_keywords.filter(o => o._id !== mik._id);
                } else {
                  new_pseudo_item.magic_item_keywords.push(mik);
                }
                onChange(new_pseudo_item);
              }}
            />
          }
        </Grid>
        { show_details &&
          <Grid
            className={cx(classes.root)}
            size={{
              xs: 12
            }}>
            <DisplayStringWithReplacements cfs={char instanceof Character ? char : undefined} display_string={mik.description} />
          </Grid>
        }
      </Grid>
    );
  } else if (mik.extra_base_type === "Spell") {
    // const extra_base_limits = mik.extra_base_limits as { levels: string[], schools: string[], onSpellList: string[] };
    // let spell_levels: number[] = [];
    // if (extra_base_limits.levels.includes("Any")) {
    //   spell_levels = [0,1,2,3,4,5,6,7,8,9];
    // } else {
    //   spell_levels = extra_base_limits.levels.map(o => {
    //     if (o === "Cantrip") {
    //       return 0;
    //     } else {
    //       return +o;
    //     }
    //   });
    // }
    // const spells = char_util.spells.filter(o => 
    //   spell_levels.includes(o.level) && 
    //   (extra_base_limits.schools.includes("Any") || 
    //     extra_base_limits.schools.includes(o.school))
    // );
    const spell_limit = mik.extra_base_limit.limits.length > 0 && mik.extra_base_limit.limits[0] instanceof SpellLimitation ? mik.extra_base_limit.limits[0] : new SpellLimitation();
    // spell_limit.spell_ids = spells.map(o => o._id);
    return (
      <>
        <Grid container spacing={0} direction="row">
          <Grid
            className="clickable"
            onClick={() => {
              setShowDetails(!show_details);
            }}
            size={{
              xs: 4
            }}><ViewItem item={mik} /></Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectSpellBox 
              name="Spell"
              value={extra_spell?._id??""}
              spell_limit={spell_limit}
              // specific_spell_ids={spells.map(o => o._id)}
              onChange={(changed: string) => {
                const spell = data_util.spells.find(o => o._id === changed);
                if (spell) {
                  setExtraSpell(spell);
                  if (has_it) {
                    const new_pseudo_item = pseudo_item.clone();
                    new_pseudo_item.extra = changed;
                    new_pseudo_item.extra_spell = spell;
                    onChange(new_pseudo_item);
                  }
                }
                setExtraString(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { !has_it && isDisabled(mik) ?
              <ButtonBox
                name="Incompatible"
                disabled
                onClick={() => {}}
              />
            :
              <ToggleButtonBox
                name={ has_it ? "Included" : "Include" }
                value={has_it}
                disabled={ extra_string === "" || extra_spell === null }
                onToggle={() => {
                  const new_pseudo_item = pseudo_item.clone();
                  if (has_it) {
                    new_pseudo_item.magic_item_keywords = new_pseudo_item.magic_item_keywords.filter(o => o._id !== mik._id);
                    new_pseudo_item.extra = "";
                    new_pseudo_item.extra_spell = null;
                  } else {
                    new_pseudo_item.magic_item_keywords.push(mik);
                    new_pseudo_item.extra = extra_string;
                    new_pseudo_item.extra_spell = extra_spell;
                  }
                  onChange(new_pseudo_item);
                }}
              />
            }
          </Grid>
          { show_details &&
            <Grid
              className={cx(classes.root)}
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements cfs={char instanceof Character ? char : undefined} display_string={mik.description} />
            </Grid>
          }
          { extra_spell && 
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ToggleButtonBox
                  name="Show Spell Description"
                  value={show_spell}
                  onToggle={() => { setShowSpell(!show_spell); }}
                />
              </Grid>
              { show_spell && 
                <Grid
                  className={cx(classes.root)}
                  size={{
                    xs: 12
                  }}>
                  <DisplayStringWithReplacements cfs={char instanceof Character ? char : undefined} display_string={extra_spell.description} />
                </Grid>
              }
            </> 
          }
        </Grid>
      </>
    );
  } else {
    let extra_options: string[] = [];
    if (mik.extra_base_type === "Dragon Breed") {
      const extra_base_limits = mik.extra_base_limits as string[];
      extra_options = extra_base_limits.length > 0 ? extra_base_limits : ALL_DRAGONS;
    } else if (mik.extra_base_type === "Creature Type") {
      const extra_base_limits = mik.extra_base_limits as string[];
      extra_options = extra_base_limits.length > 0 ? extra_base_limits : CREATURE_TYPES;
    }
    const has_it = pseudo_item.keyword_ids.includes(mik._id);
    // if (mik.name.includes("Dragon's Wrath")) {
    //   debugger;
    // }
    return (
      <>
        <Grid container spacing={0} direction="row">
          <Grid
            className="clickable"
            onClick={() => {
              setShowDetails(!show_details);
            }}
            size={{
              xs: 4
            }}><ViewItem item={mik} /></Grid>
          <Grid
            size={{
              xs: 4
            }}>
            <SelectStringBox
              name={mik.extra_base_type}
              options={extra_options}
              value={extra_string}
              onChange={(changed: string) => {
                if (has_it) {
                  const new_pseudo_item = pseudo_item.clone();
                  new_pseudo_item.extra = changed;
                  onChange(new_pseudo_item);
                }
                setExtraString(changed);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 4
            }}>
            { !has_it && isDisabled(mik) ?
              <ButtonBox
                name="Incompatible"
                disabled
                onClick={() => {}}
              />
            :
              <ToggleButtonBox
                name={ has_it ? "Included" : "Include" }
                value={has_it}
                disabled={ extra_string === "" }
                onToggle={() => {
                  const new_pseudo_item = pseudo_item.clone();
                  if (has_it) {
                    new_pseudo_item.magic_item_keywords = new_pseudo_item.magic_item_keywords.filter(o => o._id !== mik._id);
                    new_pseudo_item.extra = "";
                    new_pseudo_item.extra_spell = null;
                  } else {
                    new_pseudo_item.magic_item_keywords.push(mik);
                    new_pseudo_item.extra = extra_string;
                  }
                  onChange(new_pseudo_item);
                }}
              />
            }
          </Grid>
          { show_details &&
            <Grid
              className={cx(classes.root)}
              size={{
                xs: 12
              }}>
              <DisplayStringWithReplacements cfs={char instanceof Character ? char : undefined} display_string={mik.description} />
            </Grid>
          }
        </Grid>
      </>
    );
  }
}
